'use client';

import { Timestamp } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { useSetState } from 'minimal-shared/hooks';
import { useRef, useMemo, useEffect, useCallback } from 'react';
import * as ChannelService from '@channel.io/channel-web-sdk-loader';

import { paths } from 'src/routes/paths';
import { useRouter, usePathname } from 'src/routes/hooks';

import { log } from 'src/utils/logger';
import { normalizePlanId } from 'src/utils/plan/plan-utils';
import { getOrCreateGuestId } from 'src/utils/auth/guest-id';
import { resolveLandingWorkspaceId } from 'src/utils/workspace/workspace-landing';
import { withReturnTo, pendingReturnToOr } from 'src/utils/auth/return-to-storage';
import { findCurrentWorkspace, checkSubscriptionStatus } from 'src/utils/util-user';

import axios from 'src/lib/axios';
import { CONFIG } from 'src/global-config';
import { sendWelcomeEmail } from 'src/actions/email';
import { getQueryClient } from 'src/lib/query-client';
import { reportSignup } from 'src/actions/attribution';
import { fsCall } from 'src/actions/firestore/dispatch';
import { workspaceKeys } from 'src/query-keys/workspace';
import { IWorkspacePlan } from 'src/constants/plan-service';
import { reportOutreachSignup } from 'src/actions/outreach';
import { setElsAccessUser } from 'src/lib/els/els-access-gate';
import { getFirebaseAuth } from 'src/lib/firebase/firebase-connection';
import { slack_send, slack_channel, slack_trigger } from 'src/lib/slack';

import { AuthContext } from '../auth-context';

import type { AuthUser, UserWorkspace } from '../auth-context-value';



const CONTACT_VERIFICATION_PATH = paths.auth.contactVerification;

/**
 * `lastOnline` 갱신 최소 간격 — 라우트 이동마다 쓰지 않는다.
 *
 * 워크스페이스 «읽기» 는 TanStack 캐시가 60초 dedup 하지만 이 «쓰기» 는 캐시 앞에 있어,
 * 간격을 두지 않으면 이동 1회당 쓰기 1건이 나간다. 접속 흔적을 남기는 게 목적이지
 * 분 단위 정확도가 필요한 값이 아니다.
 *
 * 모듈 스코프인 이유: Provider 가 다시 마운트돼도 「마지막으로 쓴 시각」은 살아 있어야 한다.
 */
const CONNECTION_PING_INTERVAL_MS = 10 * 60 * 1000;
let lastConnectionPingAt = 0;

// ----------------------------------------------------------------------

/**
 * 세션 사용자 스냅샷 — firebase user + 프로필 문서 + workspaces.
 * 모양의 주인은 `auth/context/auth-context-value.ts` 의 `AuthUser` 다(util-user 와 같은 규약) —
 * 여기는 provider 전용 확장(accessToken 등)만 얹는다.
 */
type SessionUser = AuthUser & { accessToken?: string; appVersion?: string; photoUrl?: string; referral_code?: string }
type WsRow = UserWorkspace & { ownerId?: string } & Record<string, unknown>

export function AuthProvider({ children }: { children?: React.ReactNode }) {

  const { state, setState } = useSetState<{ user: SessionUser | null; loading: boolean }>({ user: null, loading: true });

  // Provider 바깥이라 useQueryClient를 못 쓴다 — 공유 싱글턴을 직접 쓴다.
  const queryClient = getQueryClient();
  const router = useRouter();
  const pathname = usePathname();
  // const DB = getFirestore(firebaseApp);

  const bootChannelService = (user: SessionUser | null, profile: SessionUser | null) => {
    const channelConfig: {
      pluginKey: string
      memberId?: string
      profile?: { name?: string | null; mail?: string | null; phone_number?: string | null; referral_code?: string | null }
    } = {
      pluginKey: CONFIG.serviceApiKey.channelTalk,
    };

    if (user && profile) {
      channelConfig.memberId = user.uid;
      channelConfig.profile = {
        name: profile.display_name,
        mail: profile.email,
        phone_number: profile.phone_number || null,
        referral_code: profile.referral_code,
      };
    } else {
      const guestId = getOrCreateGuestId();
      channelConfig.memberId = `guest_${guestId}`;
      channelConfig.profile = {
        name: `Guest`,
        mail: null,
        phone_number: null,
        referral_code: null,
      };
    }

    console.log('🔍 ChannelService.boot config:', channelConfig);
    ChannelService.boot(channelConfig);
  };

  /**
   * Firebase Auth 사용자 1명에 대한 프로필·워크스페이스 동기화.
   * onAuthStateChanged는 컴포넌트당 1회만 구독하고, 재동기화는 checkUserSession에서 호출한다.
   */
  const processFirebaseUser = useCallback(async (user: SessionUser | null) => {
    console.log('user', user);

    if (user) {
      const { accessToken, uid } = user;
      const appVersion = CONFIG.appVersion || 'unknown';

      /*
        프로필 확정 — 워크스페이스 목록과 **함께** 돈다. 목록 질의는 uid 만 보므로(`memberList`)
        프로필을 기다리지 않고 먼저 뜨고, `lastWorkspace`·`isAdmin` 이 필요한 뒷부분만
        `profile` 약속을 기다린다(`getCheckedWorkspaceList`).
        `saved` 는 새 가입의 문서 저장 — 착지 쓰기(`adminUpdate`)는 문서가 있어야 해서 그 앞에서 기다린다.
      */
      const profileReady = (async (): Promise<{ profile: SessionUser; saved: Promise<unknown> }> => {
        const profileData = await fsCall('user.raw.get', { uid: uid as string }) as SessionUser | null;

        if (!profileData) {
          console.log('🔍 profileData', user);
          const created: SessionUser = {
            uid: user.uid,
            email: user.email,
            display_name: (user.displayName as string) || '',
            photoUrl: (user?.photoURL as string) || '',
            appVersion,
          };

          // 계정 생성 완료 시점 — 가입 기록(광고 유입·signup_completed)·영업 메일 유입 보고·가입 슬랙 알림.
          // 셋 다 기다리지 않는다(실패해도 흐름을 막지 않는다).
          // ⚠️ 가입 기록은 Users 문서에 직접 쓴다 — `user.set` 보다 먼저 돌면 처음 만들 때만 넣는 칸(`createdAt`)이 빠진다
          const saved = fsCall('user.set', { uid: uid as string, profile: created as Record<string, unknown> }).then(() => {
            void reportSignup();
            void reportOutreachSignup();
            void slack_send(slack_channel.catchmoments_new_user, {
              trigger: slack_trigger.catch_moments_user_new,
              data: {
                params1: created.uid || 'unknown',
                params2: created.display_name || 'unknown',
                params3: created.email || 'unknown',
              },
            });
          });
          return { profile: created, saved };
        }

        profileData.uid = uid;
        profileData.appVersion = appVersion;
        /*
          ⚠️ **여기서도 표시를 찍는다.** 바로 뒤에 도는 세션 확인이 같은 기록을 최소 간격으로
            거르는데(`CONNECTION_PING_INTERVAL_MS`), 이쪽이 표시를 안 찍으면 그 거르개가
            처음 한 번을 통과시켜 **화면을 열 때마다 같은 쓰기가 두 번** 나간다(실측).
          기다리지 않는다 — 뒤의 워크스페이스 목록은 이 쓰기가 적는 칸(`lastOnline`·`appVersion`)을
          읽지 않고, 창구가 실패를 삼키므로(`setUserConnection`) 부팅 갈래가 같다.
        */
        lastConnectionPingAt = Date.now();
        void fsCall('user.connection.set', { uid: uid as string, appVersion });
        return { profile: profileData, saved: Promise.resolve() };
      })();

      // uid 가 있는 갈래라 계약 union 의 객체 쪽만 온다(workspace-ops ⚠️) — 좁혀만 준다
      const workspaceList = fsCall('workspace.checkedList.get', {
        user: { uid },
        profile: profileReady.then(({ profile }) => profile as Record<string, unknown>),
      }) as Promise<{ workspaces: WsRow[]; isNew: boolean }>;
      const [{ profile, saved }, { workspaces, isNew }] = await Promise.all([profileReady, workspaceList]);
      const currentProfileData: SessionUser | null = profile;
      await saved;
      // 로그인 시 조회 결과를 캐시에 seed → 이후 네비게이션의 checkUserSessionNow가
      // staleTime 안에서 재사용해 CatchMomentsWorkspace 재조회를 막는다.
      queryClient.setQueryData(workspaceKeys.checkedList(uid), { workspaces, isNew });

      /**
       * 만료 스윕 — 어드민 진입 때 한 번, **기다리지 않고** 띄운다.
       *
       * 목록을 훑지 않고 표적 쿼리로 대상만 집는다. 실패(색인 부재 포함)는 안에서
       * 삼키므로 로그인을 막지 않는다.
       */
      if (currentProfileData?.isAdmin === true) {
        void fsCall('workspace.expired.sweep', {});
      }

      // 착지 WS 교정 — lastWorkspace 가 비었거나 내 소속이 아니면 소속 안에서 고른다.
      // 서버에도 같은 값을 적는다: 구독 관문이 Users.lastWorkspace 의 소속을 보므로 상태만 바꾸면 API 가 403 이다.
      const landingWorkspaceId = resolveLandingWorkspaceId(
        { uid: user.uid as string, isAdmin: currentProfileData?.isAdmin, lastWorkspace: currentProfileData?.lastWorkspace },
        workspaces ?? [],
      );
      if (landingWorkspaceId && landingWorkspaceId !== currentProfileData?.lastWorkspace) {
        currentProfileData!.lastWorkspace = landingWorkspaceId;
        try {
          await fsCall('user.fields.update', { uid: user.uid as string, payload: { lastWorkspace: landingWorkspaceId } });
        } catch (err) {
          // 읽기전용 서버 등 — 상태는 이미 교정됐고 스위처가 마운트 때 한 번 더 저장한다
          console.warn('[Auth] 착지 워크스페이스 저장 건너뜀:', (err as { message?: string })?.message);
        }
      }

      setState({ user: { ...user, ...currentProfileData, workspaces }, loading: false });
      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

      console.log('🔍 bootChannelService', user, currentProfileData);
      bootChannelService(user, currentProfileData);

      if (isNew) {
        // 읽기전용에선 이 쓰기가 막힌다 — 부트스트랩을 멈추지 않도록 실패를 관용한다.
        try {
          await fsCall('user.fields.update', {
            uid: user.uid as string,
            payload: { trialStartedAt: Timestamp.now() },
          });
        } catch (err) {
          console.warn('[Auth] trialStartedAt 기록 건너뜀(읽기전용 등):', (err as { message?: string })?.message);
        }

        // 관리자 계정은 웰컴 메일 대상이 아니다 — isAdmin=true면 건너뛴다.
        if (user.email && currentProfileData?.isAdmin !== true) {
          void sendWelcomeEmail(user.email).then((result: { success?: boolean; error?: unknown } | null | undefined) => {
            if (!result?.success) {
              console.warn('[Auth] welcome email skipped:', result?.error);
            }
          });
        }
      }
    } else {
      setState({ user: null, loading: false });
      delete axios.defaults.headers.common.Authorization;

      console.log('🔍 bootChannelService (guest user)');
      bootChannelService(null, null);
    }
  }, [setState, queryClient]);

  /** 로그인 직후 등 외부에서 동일 로직만 재실행할 때 사용 (리스너 추가 없음) */
  const checkUserSession = useCallback(async () => {
    try {
      await processFirebaseUser(getFirebaseAuth().currentUser as SessionUser | null);
    } catch (error) {
      console.error(error);
      setState({ user: null, loading: false });
    }
  }, [processFirebaseUser, setState]);

  useEffect(() => {
    ChannelService.loadScript();
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(getFirebaseAuth(), (user) => {
      void processFirebaseUser(user as SessionUser | null).catch((error) => {
        console.error(error);
        setState({ user: null, loading: false });
      });
    });
    return () => unsub();
  }, [processFirebaseUser, setState]);


  useEffect(() => {
    if (state?.user) {
      console.log('checkUserSession - 최종 상태(user 변경 감지):', state.user);
    }
  }, [state.user]);

  /**
   * ES 조회 관문에 만료 여부를 실어 준다 — `actions/els/els-request` 가 요청 직전에 본다.
   *
   * AuthGuard 의 만료 판정은 경로가 바뀔 때만 도니, 검색처럼 이동 없이 오래 머무는
   * 화면에서는 이것이 없으면 만료 뒤에도 조회가 계속 나간다.
   *
   * ⚠️ 넘기는 것은 **사용자 스냅샷**이지 만료 여부가 아니다. 판정은 관문이 요청 시점에
   *    `planEndTime` 과 현재 시각을 비교해서 한다 — 여기서 boolean 으로 굳혀 두면
   *    화면에 머무는 동안 만료 시점을 넘겨도 낡은 값이 남는다.
   *
   * ⚠️ 평범한 setter 라 setState 를 부르지 않는다 — deps 에 걸어도 되먹임이 없다.
   */
  useEffect(() => {
    setElsAccessUser(state.user);
  }, [state.user]);

  /**
   * 전화번호 미등록 시 서비스·랜딩 등 어디서든 연락처 인증으로 통일 (소셜 로그인 포함).
   *
   * ⚠️ `AuthGuard` 와 같은 커밋에서 **둘 다** `replace` 한다(자식 효과가 먼저). 뒤에 오는 이
   * 호출이 URL 을 정하므로 여기도 `returnTo` 를 달아야 인증을 마친 뒤 보려던 화면으로
   * 돌아간다 — 안 달면 인증 화면의 동기화 effect 가 세션의 목적지를 지운다.
   * 🔑 로그인을 시작한 자리가 세션에 남긴 돌아갈 곳이 지금 주소보다 앞선다(`pendingReturnToOr`) — 창 안 로그인은 랜딩 위에서 끝난다.
   */
  useEffect(() => {
    if (CONFIG.auth.skip) return;
    if (typeof window === 'undefined') return;
    if (state.loading) return;
    if (!state.user) return;

    const phone = state.user.phone_number;
    if (phone && phone !== '') return;

    const isDemoEntry = new URLSearchParams(window.location.search).get('demo') === 'true';
    if (isDemoEntry) return;

    if (pathname === CONTACT_VERIFICATION_PATH || pathname?.startsWith(`${CONTACT_VERIFICATION_PATH}/`)) return;

    router.replace(withReturnTo(CONTACT_VERIFICATION_PATH, pendingReturnToOr(`${pathname}${window.location.search}`)));
  }, [state.loading, state.user, pathname, router]);

  const checkUserSessionNow = useCallback(async ({ force = false } = {}) => {

    console.log('checkUserSessionNow', { force });
    const currentUser = state.user;

    if (!currentUser) {
      console.log('checkUserSessionNow - 사용자 정보 없음');
      // ⚠️ **객체로 넘긴다.** `useSetState`(minimal-shared)는 `{ ...state, ...patch }` 라
      //    patch 가 «함수»면 스프레드가 빈 병합이 되어 **조용히 무시된다**.
      //    함수형으로 두면 이 「로딩 종료」가 실행되지 않아 `loading` 이 true 로 남고,
      //    `AuthGuard` 의 `if (loading) return` 에 걸려 **SplashScreen 이 걷히지 않는다**.
      //    (React `useState` 의 setter 와 헷갈리지 말 것 — 그쪽은 함수형이 정상이다.)
      setState({ loading: false });
      return;
    }

    const appVersion = CONFIG.appVersion || 'unknown';

    // 접속 흔적은 최소 간격을 두고, 렌더를 막지 않게 기다리지 않는다.
    const now = Date.now();
    if (now - lastConnectionPingAt >= CONNECTION_PING_INTERVAL_MS) {
      lastConnectionPingAt = now;
      void fsCall('user.connection.set', { uid: currentUser.uid as string, appVersion });
    }

    // ⚠️ 네비게이션마다 호출된다 — 전 워크스페이스+캠페인 재조회를 staleTime 캐시로 dedup 한다.
    //    워크스페이스 변경·플랜/캠페인 갱신 시 invalidateQueries로 다음 호출을 강제 새로고침한다.
    const { workspaces: updatedWorkspaces } = await queryClient.fetchQuery({
      queryKey: workspaceKeys.checkedList(currentUser.uid as string),
      // uid 있는 갈래 — 계약 union 의 객체 쪽(processFirebaseUser 와 같은 좁히기)
      queryFn: () => fsCall('workspace.checkedList.get', { user: currentUser as Record<string, unknown> }) as Promise<{ workspaces: WsRow[]; isNew: boolean }>,
      // force=true면 staleTime:0으로 60초 캐시를 무시하고 강제 재조회한다.
      // (만료 화면에서 결제 후 멤버 플랜을 새로고침 없이 즉시 반영하기 위함)
      ...(force ? { staleTime: 0 } : {}),
    });

    console.log('checkUserSessionNow - 업데이트된 워크스페이스 정보:', updatedWorkspaces);

    /**
     * **캐시가 같은 목록을 돌려줬으면 새 user 를 만들지 않는다.**
     *
     * 이 함수는 라우트 이동마다 불리는데, 그 대부분은 60초 staleTime 안이라 **캐시 적중**이다.
     * 여기서 `{ ...currentUser }` 로 무조건 새 객체를 만들면 내용이 한 글자도 안 바뀌었는데
     * `user` 의 신원이 갈리고, 그것을 의존성에 둔 훅들이 통째로 다시 돈다.
     *
     * 참조 비교로 충분한 이유: 같은 `queryKey` 의 캐시 적중은 **같은 객체**를 돌려준다.
     * 값이 실제로 갱신되면 새 배열이 오므로 그때는 아래로 내려간다 —
     * **재조회 채널을 끄는 것이 아니라, 얻은 것이 같을 때 조용히 있는 것이다.**
     *
     * ⚠️ `loading: false` 는 이 갈래에서도 **반드시** 세운다. `AuthGuard` 가 `loading` 을 보고
     * SplashScreen 을 걷기 때문에, 여기서 빠뜨리면 화면이 통째로 안 뜬다.
     */
    if (updatedWorkspaces === currentUser.workspaces) {
      setState({ loading: false });
      return;
    }

    // 객체 patch — 나머지 상태 키는 그대로 보존된다(함수형 patch 는 무시된다 · 위 ⚠️)
    setState({ user: { ...currentUser, workspaces: updatedWorkspaces }, loading: false });

  }, [setState, state.user, queryClient]);

  /**
   * **워크스페이스 전환 → 목록을 새 기준으로 다시 읽는다.**
   *
   * 전환하는 자리(헤더 스위처·만료 화면·`/service/workspace/{id}` 진입)는 전부 프로필의
   * `lastWorkspace` 한 값만 바꾼다. 그런데 화면들은 「`workspaces` 에서 `lastWorkspace` 를
   * 찾은 항목」을 현재 워크스페이스로 쓴다(사이드바 플랜 판정·`usePlanAccess`·브랜드 멘션의
   * 키워드). 목록이 그대로면 ① 어드민이 멤버 아닌 워크스페이스로 바꿨을 때 항목이 없어
   * 현재 워크스페이스가 `null` 이 되고(메뉴 전부 비활성·키워드 0), ② 멤버 워크스페이스로
   * 바꿔도 `campaignList` 는 부팅 때의 현재 1건에만 붙어 있어 캠페인 메뉴가 빈다.
   *
   * 목록 조회(`checkUserSessionNow`)는 라우트가 바뀔 때만 돌고 60초 캐시라, 전환만으로는
   * 새 목록이 오지 않는다 — 새로고침해야 풀린다. 그래서 `lastWorkspace` 가 **바뀐 뒤** 한 번
   * 강제로 다시 읽는다. `getWorkspaceList` 가 새 `lastWorkspace` 로 어드민 보충 1건과 캠페인을
   * 붙여 준다.
   *
   * - 첫 부팅은 건너뛴다 — `processFirebaseUser` 가 이미 그 워크스페이스 기준으로 읽었다.
   * - 되먹임 없음: 재조회는 `workspaces` 만 바꾸고 `lastWorkspace` 는 그대로다.
   * - `checkUserSessionNow` 는 렌더마다 새로 만들어지므로 ref 로 최신 것을 부른다 —
   *   의존성에 직접 걸면 `user` 가 바뀔 때마다 효과가 돈다.
   */
  const checkUserSessionNowRef = useRef(checkUserSessionNow);
  checkUserSessionNowRef.current = checkUserSessionNow;
  const seenLastWorkspaceRef = useRef<{ uid: string; lastWorkspace: string } | null>(null);

  useEffect(() => {
    const uid = state.user?.uid;
    const lastWorkspace = state.user?.lastWorkspace;
    if (!uid || !lastWorkspace) {
      seenLastWorkspaceRef.current = null;
      return;
    }
    const seen = seenLastWorkspaceRef.current;
    seenLastWorkspaceRef.current = { uid, lastWorkspace };
    if (!seen || seen.uid !== uid || seen.lastWorkspace === lastWorkspace) return;

    void checkUserSessionNowRef.current({ force: true }).catch((error) => {
      log.error('auth/auth-provider', '워크스페이스 전환 후 목록 재조회 실패', { err: error?.message });
    });
  }, [state.user?.uid, state.user?.lastWorkspace]);

  // ⚠️ setter 군은 user 없이 부르면 그대로 터지는 계약이다 — `!` 로 그 계약을 보존한다
  const setUserWorkspaceName = useCallback((workspaceName: string, workspaceId: string | null = null) => {

    const finalWorkspaceId = workspaceId || state.user!.lastWorkspace;
    const updatedWorkspaces = state.user!.workspaces!.map((item) =>
      item.workspaceId === finalWorkspaceId ? { ...item, name: workspaceName } : item
    );
    console.log('updatedWorkspaces', finalWorkspaceId, updatedWorkspaces);


    setState({ user: { ...state.user!, workspaces: updatedWorkspaces }, loading: false });
    // 캐시도 함께 갱신 → 다음 checkUserSessionNow가 stale 캐시로 로컬 변경을 되돌리지 않게
    queryClient.setQueryData(workspaceKeys.checkedList(state.user!.uid as string), (old) => (
      old ? { ...old, workspaces: updatedWorkspaces } : old
    ));

    console.log('state.user', state.user);
  }, [setState, state.user, queryClient]);

  // 컨텍스트 계약(auth-context-value)이 두 번째 인자를 unknown 으로 선언한다 — 그대로 받고 안에서 좁힌다
  const setUpdateSubscriptionPlan = useCallback((workspaceId: string | number, rawWorkspace: unknown) => {
    const workspace = rawWorkspace as WsRow;

    const targetId = String(workspaceId);
    const updatedWorkspaces = state.user!.workspaces!.map((item) =>
      (String(item?.workspaceId ?? '') === targetId
        || String(item?.id ?? '') === targetId)
        ? workspace
        : item,
    );

    console.log('setUpdateSubscriptionPlan', updatedWorkspaces);

    setState({ user: { ...state.user!, workspaces: updatedWorkspaces }, loading: false });
    // 플랜(만료 포함) 변경을 캐시에도 반영 → 네비게이션 시 stale 캐시로 되돌아가지 않게
    queryClient.setQueryData(workspaceKeys.checkedList(state.user!.uid as string), (old) => (
      old ? { ...old, workspaces: updatedWorkspaces } : old
    ));
  }, [setState, state.user, queryClient]);


  // const setUserNewWorkspace = useCallback((workspace) => {
  //   const workspaces = [...state.user.workspaces]; // 불변성을 위해 새 배열 생성

  //   // 동일한 워크스페이스 ID가 없으면 추가
  //   const existingWorkspace = workspaces.find(item => item.workspaceId === workspace.workspaceId);

  //   if (!existingWorkspace) {
  //     workspaces.push(workspace);
  //     console.log('New workspace added:', workspace.workspaceId);
  //   } else {
  //     console.log('Workspace already exists:', workspace.workspaceId);
  //   }

  //   console.log('Total workspaces count:', workspaces.length);
  //   console.log('Updated workspaces:', workspaces);

  //   setState({ user: { ...state.user!, workspaces, lastWorkspace: workspace.workspaceId }, loading: false });
  // }, [setState, state.user]);


  const setUpdateCampaignList = useCallback((workspaceId: string, campaignList: unknown[], currentCampaignId: string | null = null) => {


    console.log('setUpdateCampaignList', workspaceId, campaignList, currentCampaignId);
    const updatedWorkspaces = state.user!.workspaces!.map((item) =>
      item.workspaceId === workspaceId ? { ...item, campaignList, currentCampaignId } : item
    );

    console.log('updatedWorkspaces', updatedWorkspaces);

    setState({ user: { ...state.user!, workspaces: updatedWorkspaces }, loading: false });
    // 캠페인 목록 변경을 캐시에도 반영
    queryClient.setQueryData(workspaceKeys.checkedList(state.user!.uid as string), (old) => (
      old ? { ...old, workspaces: updatedWorkspaces } : old
    ));
  }, [setState, state.user, queryClient]);

  /**
   * `updateProfile` 을 기다리는 쪽에 「컨텍스트에 반영됐다」를 알리는 대기열.
   *
   * setState 는 예약일 뿐이라, 부른 직후 이동하면 다음 화면의 `AuthGuard` 가 **갱신 전 user** 를
   * 읽을 수 있다(전화번호가 비어 보여 연락처 인증으로 되돌린다). 그래서 커밋된 `user` 에
   * 그 patch 가 실제로 들어 있을 때(아래 effect)에야 약속을 푼다.
   *
   * ⚠️ `flushSync` 로 동기 커밋하지 않는다 — 워크스페이스 로드 훅들이 effect 안에서 부르는데,
   *    그 자리의 `flushSync` 는 경고만 내고 예약으로 물러난다.
   */
  const profileCommitWaitersRef = useRef<Array<{ patch: Record<string, unknown>; resolve: () => void }>>([]);

  useEffect(() => {
    const waiters = profileCommitWaitersRef.current;
    if (waiters.length === 0) return;
    const user = (state.user ?? {}) as Record<string, unknown>;
    const settled = waiters.filter(({ patch }) => Object.entries(patch).every(([key, value]) => user[key] === value));
    if (settled.length === 0) return;
    profileCommitWaitersRef.current = waiters.filter((w) => !settled.includes(w));
    settled.forEach(({ resolve }) => resolve());
  }, [state.user]);

  /**
   * 프로필 일부를 컨텍스트에 반영한다(`merge` 면 문서에도 쓴다).
   *
   * @returns 새 `user` 가 **커밋된 뒤** 푸는 약속 — 인증 뒤 이동처럼 「반영을 보고 움직여야
   *          하는」 호출부는 이것을 기다린다. effect 안에서 부르는 호출부는 기다리지 않는다.
   */
  const updateProfile = useCallback(async (profileData: Record<string, unknown>, merge = false) => {
    // try {
    //   await updateUserFields(state.user!.uid as string, profile);   ← 되살릴 때는 창구를 쓸 것
    // } catch (error) {
    //   console.error(error);
    // }

    console.log("updateProfile", profileData);

    const committed = new Promise<void>((resolve) => {
      profileCommitWaitersRef.current.push({ patch: profileData, resolve });
    });

    setState({ user: { ...state.user!, ...profileData }, loading: false });

    if (merge) {
      await fsCall('user.set', { uid: state.user!.uid as string, profile: profileData });
    }

    await committed;
  }, [setState, state.user]);


  const checkSubscriptionExpired = useCallback(() => {
    const { isExpired } = checkSubscriptionStatus(state.user);

    let result = false;

    if (isExpired) {
      const updatedWorkspaces = state.user!.workspaces!.map((item) => {
        if (item.workspaceId === state.user!.lastWorkspace) {
          return {
            ...item,
            subscriptionPlan: {
              ...(item.subscriptionPlan as Record<string, unknown>),
              planStatus: IWorkspacePlan.planStatus.expired,
              planLeftDays: -1,
            },
            type: IWorkspacePlan.workspaceType.expired,
          };
        }
        return item;
      });

      setState({ user: { ...state.user!, workspaces: updatedWorkspaces }, loading: false });
      result = true;

    }

    return result;
  }, [setState, state.user]);


  const changeWorkspace = useCallback(async (workspaceId: string) => {

    setState({ user: { ...state.user!, lastWorkspace: workspaceId }, loading: false });

  }, [setState, state.user]);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;
  const currentWorkspace = findCurrentWorkspace(state.user);
  const planId = normalizePlanId(currentWorkspace?.subscriptionPlan?.planId)
    ?? normalizePlanId(currentWorkspace?.subscriptionPlan?.planName);
  const isDemo = planId === IWorkspacePlan.planId.plan_demo;

  const memoizedValue = useMemo(
    () => ({
      user: state.user
        ? {
          ...state.user,
          uid: state.user?.uid,
          accessToken: state.user?.accessToken,
        }
        : null,
      checkUserSession,
      checkUserSessionNow,
      updateProfile,
      changeWorkspace,
      setUpdateSubscriptionPlan,
      // setNewWorkspace,
      checkSubscriptionExpired,
      setUpdateCampaignList,
      setUserWorkspaceName,
      isDemo,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
    }),

    [checkUserSession, checkUserSessionNow, state.user, status, updateProfile, changeWorkspace, setUpdateSubscriptionPlan, checkSubscriptionExpired, setUpdateCampaignList, setUserWorkspaceName, isDemo]
  );

  // return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
  return <AuthContext value={memoizedValue}>{children}</AuthContext>;
}
